import { NgClass } from '@angular/common';
import { AfterViewInit, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { IBAN } from 'ibankit';
import { ConfirmationService, MessageService, PrimeTemplate } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Dialog } from 'primeng/dialog';
import { Listbox } from 'primeng/listbox';
import { Subscription } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../../core/configs/service-endpoints';
import { AuthService } from '../../../core/services/auth.service';
import { HelperService } from '../../../core/services/helper.service';
import { I18nService } from '../../../core/services/i18n.service';
import { SpsCustomIconComponent } from '../../../custom/components/sps-custom-icon/sps-custom-icon.component';
import { SpsPaymentTypeListItemComponent } from '../../../custom/components/sps-payment-type-list-item/sps-payment-type-list-item.component';
import { LayoutService } from '../../../layout/services/layout.service';
import { SidebarMenuService } from '../../../layout/services/sidebar-menu.service';
import { PaymentDataCreate } from '../../dtos/payment-type/payment-data-create';
import { AddPaymentDialogType } from '../../enums/payment/add-payment-dialog-type';
import { PaymentMethodName } from '../../enums/payment/payment-method-name';
import { AddPaymentDialog } from '../../interfaces/payment/add-payment-dialog';
import { BasePaymentMethodData } from '../../interfaces/payment/base-payment-method-data';
import { PaymentData } from '../../interfaces/payment/payment-data';
import { PaymentInvalidFlags } from '../../interfaces/payment/payment-invalid-flags';
import { PaymentSelectItemGroup } from '../../interfaces/payment/payment-select-item-group';
import { SubPaymentItemsPipe } from '../../pipes/sub-payment-items.pipe';
import { PaymentDataService } from '../../services/payment-data.service';
import { BaseComponent } from '../base/base.component';
import { AddFormPaymentMethodComponent } from './add-form-payment-method/add-form-payment-method.component';
import { AddFormPaymentComponent } from './add-form-payment-type/add-form-payment.component';

@Component({
  selector: 'app-payment-methods-view',
  imports: [
    Card,
    PrimeTemplate,
    Button,
    SpsPaymentTypeListItemComponent,
    SubPaymentItemsPipe,
    Dialog,
    AddFormPaymentComponent,
    AddFormPaymentMethodComponent,
    Listbox,
    FormsModule,
    SpsCustomIconComponent,
    NgClass
  ],
  templateUrl: './payment-methods-view.component.html'
})
export class PaymentMethodsViewComponent extends BaseComponent implements AfterViewInit {

  newPaymentData: PaymentData;
  paymentDataChangedCheckObject: PaymentData;
  addPaymentDialog: AddPaymentDialog = {visible: false};
  protected readonly PaymentDialogType = AddPaymentDialogType;
  paymentInvalidFlags: PaymentInvalidFlags;
  ibanIsValid: boolean = false;
  protected addPaymentInProgress: boolean = false;

  enablePaymentTypesDialog: boolean = false;
  inactivePaymentTypesList: PaymentSelectItemGroup[] = [];
  paymentTypesToActivate: string[] = [];

  constructor(private router: Router,
              public authService: AuthService,
              private helper: HelperService,
              public i18n: I18nService,
              private layoutService: LayoutService,
              private sidebarMenuService: SidebarMenuService,
              public paymentDataService: PaymentDataService,
              private messageService: MessageService,
              private confirmationService: ConfirmationService) {
    super();
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    );
    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-home',
          i18nLabelKey: 'MENUDASHBOARD',
          label: this.i18n.i18nLng()['MENUDASHBOARD'],
          routerLink: ['/dashboard'],
          routerLinkActive: false
        },
        {
          i18nLabelKey: 'MENUPAYMENTTYPESVIEW',
          label: this.i18n.i18nLng()['MENUPAYMENTTYPESVIEW'],
          routerLinkActive: true
        }
      ]);
    }, 50);
    /**
     * Sidebar-Menu Sync
     */
    try {
      this.layoutService.sidebarMenu.activateMenuItem(this.sidebarMenuService.findMenuItemById(this.sidebarMenuService.menuItems, 'm-payment-methods-view'));
    } catch (e) {
    }
  }

  onPaymentTypeClick(payment: PaymentData) {
    this.router.navigate(['/paymentmethod', payment.id]);
  }

  /**
   * New payment method - dialog
   * Adding a new paymentType for this Method
   * @param paymentMethod
   * @param category
   */
  addPaymentMethod(paymentMethod: BasePaymentMethodData, category: boolean = false) {

    this.newPaymentData = null;
    this.ibanIsValid = false;

    /**
     * Payment invalid data flags
     */
    this.resetPaymentInvalidFlags();

    if (category) {
      /**
       * Add payment-category
       */
      this.initNewPaymentData(AddPaymentDialogType.MAIN);
      this.addPaymentDialog.type = AddPaymentDialogType.MAIN;
      this.addPaymentDialog.i18nTitleKey = 'ADDPAYMENTMETHOD';
      this.addPaymentDialog.visible = true;
    } else {
      /**
       * Add payment-type
       */
      switch (paymentMethod.name) {
        case PaymentMethodName.CREDITCARD:
          this.initNewPaymentData(AddPaymentDialogType.CREDITCARD);
          this.addPaymentDialog.type = AddPaymentDialogType.CREDITCARD;
          this.addPaymentDialog.i18nTitleKey = 'ADDCREDITCARDPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.CREDITCARD_MOTO:
          this.initNewPaymentData(AddPaymentDialogType.CREDITCARD_MOTO);
          this.addPaymentDialog.type = AddPaymentDialogType.CREDITCARD_MOTO;
          this.addPaymentDialog.i18nTitleKey = 'ADDCREDITCARDPAYMENT_MOTO';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.SEPA:
          this.initNewPaymentData(AddPaymentDialogType.SEPA);
          this.addPaymentDialog.type = AddPaymentDialogType.SEPA;
          this.addPaymentDialog.i18nTitleKey = 'ADDSEPAPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.SEPA_BLACKLIST:
          this.initNewPaymentData(AddPaymentDialogType.SEPA_BLACKLIST);
          this.addPaymentDialog.type = AddPaymentDialogType.SEPA_BLACKLIST;
          this.addPaymentDialog.i18nTitleKey = 'ADDSEPABLACKLISTPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.KLARNA:
          this.initNewPaymentData(AddPaymentDialogType.KLARNA);
          this.addPaymentDialog.type = AddPaymentDialogType.KLARNA;
          this.addPaymentDialog.i18nTitleKey = 'ADDKLARNAPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.DIRECTTRANSFER:
          this.initNewPaymentData(AddPaymentDialogType.DIRECTTRANSFER);
          this.addPaymentDialog.type = AddPaymentDialogType.DIRECTTRANSFER;
          this.addPaymentDialog.i18nTitleKey = 'ADDDIRECTTRANSFERPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.PAYPAL:
          this.initNewPaymentData(AddPaymentDialogType.PAYPAL);
          this.addPaymentDialog.type = AddPaymentDialogType.PAYPAL;
          this.addPaymentDialog.i18nTitleKey = 'ADDPAYPALPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.APPLEPAY:
          this.initNewPaymentData(AddPaymentDialogType.APPLEPAY);
          this.addPaymentDialog.type = AddPaymentDialogType.APPLEPAY;
          this.addPaymentDialog.i18nTitleKey = 'ADDAPPLEPAYPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.GOOGLEPAY:
          this.initNewPaymentData(AddPaymentDialogType.GOOGLEPAY);
          this.addPaymentDialog.type = AddPaymentDialogType.GOOGLEPAY;
          this.addPaymentDialog.i18nTitleKey = 'ADDGOOGLEPAYPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
        case PaymentMethodName.WERO:
          this.initNewPaymentData(AddPaymentDialogType.WERO);
          this.addPaymentDialog.type = AddPaymentDialogType.WERO;
          this.addPaymentDialog.i18nTitleKey = 'ADDWEROPAYMENT';
          this.addPaymentDialog.visible = true;
          break;
      }
    }
  }

  /**
   * Cancel adding new payment data and close dialog
   */
  cancelAddPaymentData() {
    if (JSON.stringify(this.newPaymentData) !== JSON.stringify(this.paymentDataChangedCheckObject)) {
      this.confirmationService.confirm({
        position: 'center',
        message: this.i18n.i18nLng()['CONFIRMCHANGEFLAG'],
        header: this.i18n.i18nLng()['CONFIRMATION'],
        closable: true,
        closeOnEscape: true,
        icon: 'pi pi-question-circle',
        acceptButtonProps: {
          label: this.i18n.i18nLng()['YES']
        },
        rejectButtonProps: {
          label: this.i18n.i18nLng()['NO'],
          outlined: true,
          severity: 'secondary'
        },
        accept: () => {
          this.addPaymentDialog.visible = false;
        }
      });
    } else {
      this.addPaymentDialog.visible = false;
    }
  }

  /**
   * Save new payment data and close dialog
   */
  saveNewPaymentData() {
    if (!this.addPaymentInProgress) {
      this.addPaymentInProgress = true;

      /**
       * Check valid payment data
       */
      let valid: boolean = true;
      this.resetPaymentInvalidFlags();

      /**
       * in TEST-Mode IBAN is not a required field
       */
      if (this.newPaymentData.status === 'TEST' && (!this.newPaymentData?.IBAN || this.newPaymentData?.IBAN.trim().length === 0)) {
        this.ibanIsValid = true;
      }
      /** Falls eine IBAN eingegeben wurde, dann muss diese auch valide sein */
      if (this.newPaymentData?.IBAN && this.newPaymentData?.IBAN.trim().length > 0) {
        this.ibanIsValid = IBAN.isValid(this.newPaymentData?.IBAN);
        this.paymentInvalidFlags.IBAN = !this.ibanIsValid;
      }
      if (!this.newPaymentData?.paymentMethod || this.newPaymentData.paymentMethod?.trim().length === 0) {
        valid = false;
        this.paymentInvalidFlags.paymentMethod = true;
      }
      if (!this.newPaymentData?.title || this.newPaymentData.title?.trim().length === 0) {
        valid = false;
        this.paymentInvalidFlags.title = true;
      }
      if ((this.newPaymentData.status === 'LIVE') && (!this.newPaymentData?.accountHolder || this.newPaymentData.accountHolder?.trim().length === 0)) {
        valid = false;
        this.paymentInvalidFlags.accountHolder = true;
      }
      if ((this.newPaymentData.status === 'LIVE') && (!this.newPaymentData?.IBAN || this.newPaymentData.IBAN?.trim().length === 0 || !this.ibanIsValid)) {
        valid = false;
        this.paymentInvalidFlags.IBAN = true;
      }
      if ((this.newPaymentData.status === 'LIVE') && (!this.newPaymentData?.BIC || this.newPaymentData.BIC?.trim().length === 0)) {
        valid = false;
        this.paymentInvalidFlags.BIC = true;
      }
      if ((this.newPaymentData.status === 'LIVE') && (!this.newPaymentData?.bank || this.newPaymentData.bank?.trim().length === 0)) {
        valid = false;
        this.paymentInvalidFlags.bank = true;
      }
      if (this.newPaymentData.emailNotification && (!this.newPaymentData?.emailAddress || this.newPaymentData.emailAddress?.trim().length === 0)) {
        valid = false;
        this.paymentInvalidFlags.emailAddress = true;
      }
      if (this.newPaymentData?.emailAddress) {
        this.newPaymentData.emailAddress = this.newPaymentData.emailAddress.trim();
        if (this.newPaymentData?.emailAddress.length > 0) {
          if (!this.helper.isValidEmailList(this.newPaymentData.emailAddress)) {
            valid = false;
            this.paymentInvalidFlags.invalidEmailFormat = true;
          }
        }
      }
      if (this.newPaymentData.paymentMethod === PaymentMethodName.PAYPAL) {
        if (this.newPaymentData?.payPalEmailAddress && (this.newPaymentData?.payPalEmailAddress.length > 0)) {
          if (!this.helper.isValidEmail(this.newPaymentData.payPalEmailAddress)) {
            valid = false;
            this.paymentInvalidFlags.payPalEmailAddress = true;
          }
        }
      }

      if (valid && this.ibanIsValid) {
        let paymentDataDto: PaymentDataCreate = {
          paymentMethod: this.newPaymentData.paymentMethod,
          customIcon: false,
          title: this.newPaymentData.title,
          status: this.newPaymentData.status,
          accountHolder: this.newPaymentData.accountHolder,
          IBAN: this.newPaymentData.IBAN,
          BIC: this.newPaymentData.BIC,
          bank: this.newPaymentData.bank,
          pspMerchantId: this.newPaymentData.pspMerchantId,
          emailNotification: this.newPaymentData.emailNotification,
          emailAddress: this.newPaymentData.emailAddress,
          isChargebackCard: this.newPaymentData.isChargebackCard,
        };
        paymentDataDto = this.paymentDataService.setIconAndProvider(paymentDataDto);
        this.paymentDataService.create(SERVICE_ENDPOINTS.CREATE_PAYMENT_TYPE, paymentDataDto).subscribe({
          next: (result) => {
            if (result.successful) {
              const subscription: Subscription = this.paymentDataService.initTenantPaymentData().subscribe(fetched => {
                subscription.unsubscribe();
                if (fetched) {
                  this.sidebarMenuService.refreshPaymentMenuItems();
                  this.addPaymentInProgress = false;
                  this.addPaymentDialog.visible = false;
                  /**
                   * Refresh view
                   */
                  this.loading = true;
                  setTimeout(() => {
                    this.loading = false;
                  }, 10);
                  this.messageService.add({
                    life: 3500,
                    severity: 'success',
                    summary: this.i18n.i18nLng()['ADDPAYMENTTYPE'],
                    detail: this.i18n.i18nLng()['HTTPDATACREATESUCCESS']
                  });
                }
              });
            } else {
              this.loading = false;
              this.addPaymentInProgress = false;
              this.messageService.add({
                sticky: true,
                severity: 'error',
                summary: this.i18n.i18nLng()['HTTPDATAERROR'],
                detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
              });
            }
          },
          error: (error) => {
            this.addPaymentInProgress = false;
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: this.i18n.i18nLng()['HTTPDATAERROR'],
              detail: error.message
            });
          }
        });
      } else {
        this.addPaymentInProgress = false;
      }
    }
  }

  /**
   * Reset all invalid flags for payment data
   * @private
   */
  private resetPaymentInvalidFlags() {
    this.paymentInvalidFlags = {
      paymentMethod: false,
      title: false,
      accountHolder: false,
      IBAN: false,
      BIC: false,
      bank: false,
      emailAddress: false,
      invalidEmailFormat: false,
      payPalEmailAddress: false,
    };
  }

  /**
   * Initialization of new Payment-Date for
   * the specific Payment-Method
   * @private
   */
  private initNewPaymentData(paymentMethod: AddPaymentDialogType) {

    /**
     * Data for all Payment-Methods
     */
    this.newPaymentData = {
      id: null,
      status: 'TEST'
    };

    /**
     * Specific Payment-Method Data
     */
    switch (paymentMethod) {
      case AddPaymentDialogType.MAIN:
        this.newPaymentData.paymentMethod = null;
        break;
      case AddPaymentDialogType.SEPA:
        this.newPaymentData.paymentMethod = PaymentMethodName.SEPA;
        break;
      case AddPaymentDialogType.SEPA_BLACKLIST:
        this.newPaymentData.paymentMethod = PaymentMethodName.SEPA_BLACKLIST;
        break;
      case AddPaymentDialogType.CREDITCARD:
        this.newPaymentData.paymentMethod = PaymentMethodName.CREDITCARD;
        break;
      case AddPaymentDialogType.CREDITCARD_MOTO:
        this.newPaymentData.paymentMethod = PaymentMethodName.CREDITCARD_MOTO;
        break;
      case AddPaymentDialogType.WERO:
        this.newPaymentData.paymentMethod = PaymentMethodName.WERO;
        break;
      case AddPaymentDialogType.KLARNA:
        this.newPaymentData.paymentMethod = PaymentMethodName.KLARNA;
        break;
      case AddPaymentDialogType.DIRECTTRANSFER:
        this.newPaymentData.paymentMethod = PaymentMethodName.DIRECTTRANSFER;
        break;
      case AddPaymentDialogType.PAYPAL:
        this.newPaymentData.paymentMethod = PaymentMethodName.PAYPAL;
        break;
      case AddPaymentDialogType.APPLEPAY:
        this.newPaymentData.paymentMethod = PaymentMethodName.APPLEPAY;
        break;
      case AddPaymentDialogType.GOOGLEPAY:
        this.newPaymentData.paymentMethod = PaymentMethodName.GOOGLEPAY;
        break;
    }
    /**
     * needed for confirmation-dialog on closing
     * dialog without saving the changes
     */
    this.paymentDataChangedCheckObject = this.helper.jsonCopy(this.newPaymentData);
  }

  /**
   * Checked the validity of the IBAN
   * @param valid
   */
  onIbanValidated(valid: boolean) {
    this.ibanIsValid = valid;
    this.paymentInvalidFlags.IBAN = !valid;
  }

  /**
   * Request activation of new payment types:
   * show inactive TEST payment types for multi-selection
   */
  enablePaymentTypes() {
    const subscription: Subscription = this.paymentDataService.initTenantPaymentData().subscribe({
      next: () => {
        subscription.unsubscribe();
        this.inactivePaymentTypesList = [];
        this.paymentTypesToActivate = [];
        this.paymentDataService.paymentMethods.map(paymentMethod => {
          if (paymentMethod.hasPaymentTypes) {
            const inactivePaymentTypes = this.paymentDataService.paymentTypes.filter(
              paymentType => paymentType.paymentMethod === paymentMethod.name
                && !paymentType.active
                && paymentType.status === 'TEST'
                && !paymentType.activationRequested
            );
            if (inactivePaymentTypes && inactivePaymentTypes.length > 0) {
              const selectItemGroup: PaymentSelectItemGroup = {
                label: this.i18n.i18nLng()[paymentMethod.name].toUpperCase(),
                value: paymentMethod.name,
                icon: paymentMethod.icon,
                customIcon: paymentMethod.customIcon,
                items: inactivePaymentTypes.map(paymentType => {
                  return {
                    label: paymentType.title,
                    value: paymentType.id
                  };
                })
              };
              this.inactivePaymentTypesList.push(selectItemGroup);
            }
          }
        });
        this.enablePaymentTypesDialog = true;
      }
    });
  }

  /**
   * Cancel enable payment types dialog
   * @protected
   */
  protected cancelEnablePaymentTypes() {
    this.enablePaymentTypesDialog = false;
  }

  /**
   * TODO set new workflow status for each payment method that should be enabled
   * @protected
   */
  protected requestEnablePaymentTypes() {

    this.paymentDataService.update(SERVICE_ENDPOINTS.UPDATE_PAYMENT_TYPES_ACTIVATION_REQUEST, this.paymentTypesToActivate).subscribe({
      next: result => {
        if (result.successful) {
          this.enablePaymentTypesDialog = false;
          this.messageService.add({
            life: 4000,
            severity: 'info',
            summary: this.i18n.i18nLng()['ENABLEPAYMENTTYPESTITLE'],
            detail: this.i18n.i18nLng()['ENABLEPROCESSINFO']
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
      }
    })
  }

}
